import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ActionStepMenu.css';

/**
 * 3-dot overflow menu — visual match for Goals card menus.
 * Panel is portaled + fixed so parent overflow/scroll never clips or expands.
 */
export default function ActionStepMenu({
    open = false,
    opensUp = false,
    menuRef,
    label = 'action',
    onToggle,
    showComplete = false,
    onComplete,
    onEdit,
    onRemove,
    showRemove = true,
}) {
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const [coords, setCoords] = useState(null);

    const syncMenuRef = () => {
        if (!menuRef) return;
        menuRef.current = {
            contains: (node) =>
                Boolean(
                    wrapRef.current?.contains(node) ||
                        panelRef.current?.contains(node)
                ),
        };
    };

    const updatePosition = () => {
        const btn = wrapRef.current?.querySelector('.dv2-action-menu__btn');
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const panelH = panelRef.current?.offsetHeight || 128;
        const gap = 4;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp =
            opensUp || (spaceBelow < panelH + gap && rect.top > spaceBelow);

        setCoords({
            top: openUp ? rect.top - gap : rect.bottom + gap,
            right: Math.max(8, window.innerWidth - rect.right),
            openUp,
        });
        syncMenuRef();
    };

    useLayoutEffect(() => {
        if (!open) {
            setCoords(null);
            if (menuRef) menuRef.current = null;
            return undefined;
        }

        updatePosition();
        // Second pass after panel mounts with real height
        const raf = window.requestAnimationFrame(updatePosition);

        const onReposition = () => updatePosition();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
            if (menuRef) menuRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, opensUp, showComplete, showRemove]);

    const panel =
        open && coords
            ? createPortal(
                  <div
                      ref={(node) => {
                          panelRef.current = node;
                          syncMenuRef();
                      }}
                      className={`dv2-action-menu__panel is-fixed${
                          coords.openUp ? ' is-up' : ''
                      }`}
                      role="menu"
                      style={{
                          top: coords.openUp ? 'auto' : `${coords.top}px`,
                          bottom: coords.openUp
                              ? `${window.innerHeight - coords.top}px`
                              : 'auto',
                          right: `${coords.right}px`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                  >
                      {showComplete ? (
                          <button
                              type="button"
                              role="menuitem"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  onComplete?.();
                              }}
                          >
                              Complete
                          </button>
                      ) : null}
                      <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => {
                              e.stopPropagation();
                              onEdit?.();
                          }}
                      >
                          Edit
                      </button>
                      {showRemove ? (
                          <button
                              type="button"
                              role="menuitem"
                              className="dv2-action-menu__danger"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  onRemove?.();
                              }}
                          >
                              Remove
                          </button>
                      ) : null}
                  </div>,
                  document.body
              )
            : null;

    return (
        <div
            className="dv2-action-menu"
            ref={wrapRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                className="dv2-action-menu__btn"
                aria-label={`Actions for ${label}`}
                aria-expanded={open}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle?.();
                }}
            >
                ⋯
            </button>
            {panel}
        </div>
    );
}
